/**
 * API · core.channels + core.channel_members
 *
 * RLS gobierna la visibilidad. El cliente solo pide, el server filtra.
 * Convención: `area` channels NO se crean desde acá — vienen seedados +
 * sincronizados por trigger con employee_assignments.
 */
import { supabase } from '@/lib/supabase';
import type {
  ChannelMembersRow,
  ChannelsInsert,
  ChannelsRow,
} from '@/types/database.types';

// Casts a any: tablas chat NO en database.types.ts hand-curated.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function listChannels(): Promise<ChannelsRow[]> {
  const { data, error } = await db
    .from('channels')
    .select('*')
    .eq('is_archived', false)
    .order('kind', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChannelsRow[];
}

export async function getChannel(id: string): Promise<ChannelsRow | null> {
  const { data, error } = await db
    .from('channels')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as ChannelsRow | null) ?? null;
}

export async function createChannel(input: ChannelsInsert): Promise<ChannelsRow> {
  const { data, error } = await db
    .from('channels')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data as ChannelsRow;
}

/**
 * Edita los datos del canal: nombre, descripción (`topic`), archivado y a qué
 * proyecto pertenece.
 *
 * La RLS decide quién puede (`channels_update_owner_or_admin`): dueño, admin
 * del canal, o `channels.manage_all`. Acá no se replica esa lógica.
 */
export async function updateChannel(
  id: string,
  patch: Partial<
    Pick<ChannelsRow, 'name' | 'topic' | 'is_archived' | 'project_id'>
  >,
): Promise<ChannelsRow> {
  const { data, error } = await db
    .from('channels')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ChannelsRow;
}

export async function listChannelMembers(channelId: string): Promise<ChannelMembersRow[]> {
  const { data, error } = await db
    .from('channel_members')
    .select('*')
    .eq('channel_id', channelId)
    .order('joined_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChannelMembersRow[];
}

/**
 * Sumar gente a un canal. La RLS lo permite al owner/admin del canal (o a
 * quien tenga channels.manage_all); el llamador ya gatea la UI por eso.
 *
 * Idempotente por PK (channel_id, user_id): si alguien ya estaba, se ignora.
 * Los canales `area` NO se tocan desde acá — los sincroniza el trigger con
 * core.employee_assignments.
 */
export async function addChannelMembers(
  channelId: string,
  userIds: string[],
): Promise<void> {
  if (userIds.length === 0) return;
  const { error } = await db
    .from('channel_members')
    .upsert(
      userIds.map((user_id) => ({ channel_id: channelId, user_id, role: 'member' })),
      { onConflict: 'channel_id,user_id', ignoreDuplicates: true },
    );
  if (error) throw error;
}

/** Quitar a alguien del canal (la RLS exige ser owner/admin, o uno mismo). */
export async function removeChannelMember(
  channelId: string,
  userId: string,
): Promise<void> {
  const { error } = await db
    .from('channel_members')
    .delete()
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function markChannelRead(channelId: string, userId: string): Promise<void> {
  const { error } = await db
    .from('channel_members')
    .update({ last_read_at: new Date().toISOString() })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Crear o reusar un DM 1-on-1 con otro user.
 * Busca un canal kind='dm' donde exactamente esos 2 users sean miembros;
 * si no existe, lo crea + agrega al otro como member (el creador queda owner
 * por trigger tg_seed_area_channel_members).
 */
export async function getOrCreateDm(otherUserId: string): Promise<ChannelsRow> {
  const { data: session } = await supabase.auth.getSession();
  const myUserId = session.session?.user?.id;
  if (!myUserId) throw new Error('Sin sesión activa');
  if (myUserId === otherUserId) throw new Error('No podés iniciar DM con vos mismo');

  // Buscar DM existente compartido por ambos
  const { data: candidates, error: e1 } = await db
    .from('channels')
    .select('id, channel_members!inner(user_id)')
    .eq('kind', 'dm');
  if (e1) throw e1;
  const existing = (candidates ?? []).find((c: { id: string; channel_members: { user_id: string }[] }) => {
    const uids = new Set(c.channel_members.map((m) => m.user_id));
    return uids.size === 2 && uids.has(myUserId) && uids.has(otherUserId);
  });
  if (existing) {
    return (await getChannel(existing.id))!;
  }

  // Crear nuevo DM (trigger me agrega como owner)
  const dm = await createChannel({ kind: 'dm', created_by: myUserId });
  // Agregar al otro
  const { error: e2 } = await db
    .from('channel_members')
    .insert({ channel_id: dm.id, user_id: otherUserId, role: 'member' });
  if (e2) throw e2;
  return dm;
}
